import { reportError } from "@/utils/reportError";
import { getMondayOfWeekCH, shiftDayString } from '@/utils/date';
import { supabase } from '@/utils/supabase';
import { useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';

interface DayActivity {
  day: string;
  points: number;
  dayOfWeek: number;
}

const EMPTY_WEEK: DayActivity[] = [
  { day: 'Lun.', points: 0, dayOfWeek: 1 },
  { day: 'Mar.', points: 0, dayOfWeek: 2 },
  { day: 'Mer.', points: 0, dayOfWeek: 3 },
  { day: 'Jeu.', points: 0, dayOfWeek: 4 },
  { day: 'Ven.', points: 0, dayOfWeek: 5 },
];

/**
 * Maps a YYYY-MM-DD string (DB date) to a Mon-Fri index 0-4, or -1 if
 * weekend. Computed via UTC math so the result is independent of the
 * device timezone (otherwise `new Date('YYYY-MM-DD').getDay()` shifts in
 * negative offsets, putting Friday actions in Thursday's bucket).
 */
function dateStringToWeekIndex(dateString: string): number {
  const [y, m, d] = dateString.split('-').map(Number);
  if (!y || !m || !d) return -1;
  const utcDayOfWeek = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // Sun=0..Sat=6
  // Mon=1 → 0, Tue=2 → 1, ..., Fri=5 → 4
  if (utcDayOfWeek >= 1 && utcDayOfWeek <= 5) return utcDayOfWeek - 1;
  return -1;
}

export function useWeeklyActivity() {
  const [weeklyActivity, setWeeklyActivity] = useState<DayActivity[]>(EMPTY_WEEK);
  const [loading, setLoading] = useState(true);

  const fetchWeeklyActivity = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setWeeklyActivity(EMPTY_WEEK);
        return;
      }

      // Week range in Europe/Zurich (Monday → Friday)
      const mondayStr = getMondayOfWeekCH();
      const fridayStr = shiftDayString(mondayStr, 4);

      const { data: actions, error } = await supabase
        .from('daily_actions')
        .select('action_date, points_earned')
        .eq('user_id', user.id)
        .gte('action_date', mondayStr)
        .lte('action_date', fridayStr);

      if (error) throw error;

      const pointsByDay: number[] = [0, 0, 0, 0, 0];
      actions?.forEach((action) => {
        const idx = dateStringToWeekIndex(action.action_date as string);
        if (idx >= 0) {
          pointsByDay[idx] += action.points_earned ?? 0;
        }
      });

      setWeeklyActivity(
        EMPTY_WEEK.map((d, i) => ({ ...d, points: pointsByDay[i] }))
      );
    } catch (error) {
      reportError(error);
      setWeeklyActivity(EMPTY_WEEK);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch whenever the Profile screen comes back into focus so freshly
  // validated actions show up without needing an app restart.
  useFocusEffect(
    useCallback(() => {
      fetchWeeklyActivity();
    }, [fetchWeeklyActivity])
  );

  return { weeklyActivity, loading, refetch: fetchWeeklyActivity };
}
